import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
  Spinner,
  Alert,
} from "reactstrap";
import { CardContent } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PadInfoModal from "./PadInfoModal";
import PadHistory from "./PadHistory";
import { listPadEntries, listArchivedPadEntries, voidPadEntry, getUnitPadStatus } from "./api";
import { categoryMeta, formatEntryDetails, isScheduled, money, PAD_STATE_META, SOURCE_META } from "./padInfoTypes";
import { formatYmd } from "../../../../../../utils/dateOnly";

const shortDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA", { day: "numeric", month: "short", year: "numeric" });
};

const CategoryPill = ({ category }) => {
  const m = categoryMeta(category);
  return (
    <span style={{ background: m.bg, color: m.color, fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}>
      <FeatherIcon icon={m.icon} size={12} />
      {m.label}
    </span>
  );
};

const StatePill = ({ status }) => {
  if (!status) return null;
  const m = PAD_STATE_META[status.pad_state] || PAD_STATE_META.NO_PAD;
  const src = SOURCE_META[status.source];
  const title = [
    m.label,
    status.rent != null ? money(status.rent) : null,
    status.next_note || null,
    status.source !== "DATA" && src ? src.label : null,
    status.verified ? "Verified by accounting" : status.source === "DATA" ? "Awaiting accounting verification" : null,
  ].filter(Boolean).join(" · ");
  return (
    <span title={title} style={{ background: m.bg, color: m.color, fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 10, verticalAlign: "middle" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.color, display: "inline-block" }} />
      {m.label}
      {status.rent != null && status.pad_state === "ON_PAD" ? ` · ${money(status.rent)}` : ""}
      {/* A signed addendum still ahead: the rent it sets, and from when. Gone once the date arrives. */}
      {status.rent_change && !status.rent_change.applied && status.rent_change.new_rent != null && status.rent_change.new_rent !== status.rent
        ? ` → ${money(status.rent_change.new_rent)} from ${formatYmd(status.rent_change.effective_date)}`
        : ""}
      {status.source === "DOCUMENT" ? " · doc only" : ""}
    </span>
  );
};

/**
 * Table + actions for one unit's PAD Info entries.
 *  - live entries for the current tenancy (default)
 *  - "Show history" adds voided entries and entries of archived tenancies (dimmed)
 *  - archivedItemId mode (Archived Mastersheet preview): read-only list of that tenancy's entries
 */
const PadInfoList = ({
  itemId, item, archivedItemId, propertyId, propertyName, buildingName, unitNo, tenantNames, incoming,
  readOnly, canWrite, onStatusChange, unitStatus,
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // Which entries have their history open. A Set keeps several expanded at once, which is how
  // people actually compare two changes.
  const [openHistory, setOpenHistory] = useState(() => new Set());
  const toggleHistory = (id) =>
    setOpenHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!itemId && !archivedItemId) return;
    setLoading(true);
    try {
      const res = archivedItemId ? await listArchivedPadEntries(archivedItemId) : await listPadEntries(itemId, showHistory);
      setEntries(res.data?.data || []);
    } catch (e) {
      toast.error("Failed to load PAD info");
    } finally {
      setLoading(false);
    }
  }, [itemId, archivedItemId, showHistory]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    onStatusChange && onStatusChange();
    // Let the floating notification center re-poll immediately.
    try { window.dispatchEvent(new Event("rdc-pad-changed")); } catch (_) { /* older browsers */ }
  };

  const visible = useMemo(() => (showHistory || archivedItemId ? entries : entries.filter((e) => !e.voided && !e.tenancy_archived)), [entries, showHistory, archivedItemId]);
  const hiddenCount = entries.length - visible.length;

  const handleVoid = async (e) => {
    const confirm = await Swal.fire({
      title: "Void this PAD info?",
      text: "It stays in the history but no longer counts as the unit's PAD state.",
      input: "text",
      inputPlaceholder: "Reason (optional)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, void",
      confirmButtonColor: "#a32d2d",
    });
    if (!confirm.isConfirmed) return;
    setBusyId(e._id);
    try {
      await voidPadEntry(e._id, confirm.value || "");
      toast.success("PAD info voided");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to void");
    } finally {
      setBusyId(null);
    }
  };

  const canAdd = canWrite && !readOnly && !archivedItemId && !!itemId;
  // Archived tenancies still take one kind of update: the PAD Stop nobody recorded
  // before the unit was archived. The modal opens in archived mode (Stop only).
  const canAddArchived = canWrite && !readOnly && !!archivedItemId;

  return (
    <div>
      <style>{`
        .pdi-wrap { border: 1px solid #E4E9F2; border-radius: 12px; overflow: hidden; background: #fff; }
        .pdi-scroll { overflow-x: auto; }
        .pdi-table { width: 100%; min-width: 1000px; border-collapse: collapse; font-size: 13.5px; color: #0F1F35; }
        .pdi-table thead th { background: #F7F9FC; color: #64748B; font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
          text-transform: uppercase; text-align: left; padding: 11px 14px; border-bottom: 1px solid #E4E9F2; white-space: nowrap; }
        .pdi-table thead th.num { text-align: right; }
        .pdi-table tbody td { padding: 12px 12px; vertical-align: top; border-bottom: 1px solid #EEF2F7; }
        .pdi-table thead th { padding-left: 12px; padding-right: 12px; }
        .pdi-table tbody tr:last-child td { border-bottom: none; }
        .pdi-table tbody tr:hover { background: #FAFCFF; }
        .pdi-table tr.muted td { opacity: 0.55; }
        .pdi-table td.num { text-align: right; }
        .pdi-date { white-space: nowrap; color: #3D4C63; font-variant-numeric: tabular-nums; }
        .pdi-details { min-width: 280px; max-width: 460px; line-height: 1.45; overflow-wrap: anywhere; }
        .pdi-notes { min-width: 150px; max-width: 240px; color: #3D4C63; line-height: 1.45; white-space: pre-wrap; overflow-wrap: anywhere; }
        .pdi-table td.pdi-cat { min-width: 150px; }
        .pdi-attach { display: inline-flex; align-items: center; gap: 4px; }
        .pdi-hist-toggle { border: none; background: none; padding: 2px 0 0; margin: 0; font-size: 11.5px; font-weight: 600;
          color: #1A6FB5; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; font-family: inherit; }
        .pdi-hist-toggle:hover { text-decoration: underline; }
        .pdi-hist-row > td { background: #FBFCFE; padding: 10px 14px 12px; }
        .pdi-table th.w-sno { width: 46px; } .pdi-table th.w-eff { width: 112px; } .pdi-table th.w-act { width: 138px; }
        .pdi-meta { font-size: 11.5px; color: #8A94A6; margin-top: 3px; }
        .pdi-id { font-size: 11px; color: #8A94A6; font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace; margin-top: 3px; letter-spacing: 0.02em; }
        .pdi-actions { display: flex; justify-content: flex-end; align-items: center; gap: 8px; white-space: nowrap; }
        .pdi-btn { height: 30px; padding: 0 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px; border: 1px solid #D6DEEA; background: #fff; color: #33415C; font-family: inherit;
          transition: background .12s ease-out, border-color .12s ease-out; }
        .pdi-btn:hover:not(:disabled) { background: #F1F5FA; border-color: #B9C6D8; }
        .pdi-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .pdi-btn-danger { color: #A32D2D; border-color: #EDD3D3; }
        .pdi-btn-danger:hover:not(:disabled) { background: #FCF1F1; border-color: #DFB3B3; }
        .pdi-tag { font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: 6px; background: #EEF0F2; color: #5F5E5A; margin-left: 6px; text-transform: uppercase; letter-spacing: .04em; }
        .pdi-tag-sched { background: #E3F2FD; color: #1565C0; }
        .pdi-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
        .pdi-link { background: none; border: none; padding: 0; cursor: pointer; font-size: 12.5px; font-weight: 600; color: #1A6FB5; display: inline-flex; align-items: center; gap: 5px; font-family: inherit; }
        .pdi-link:hover { color: #0C447C; }
      `}</style>

      <div className="pdi-toolbar">
        {!archivedItemId && (
          <button type="button" className="pdi-link" onClick={() => setShowHistory((s) => !s)}>
            <FeatherIcon icon={showHistory ? "eye-off" : "clock"} size={13} />
            {showHistory ? "Hide history" : "Show history (voided & previous residents)"}
          </button>
        )}
        {archivedItemId && (
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            {canAddArchived ? "PAD history for this archived tenancy — a PAD Stop can still be recorded." : "PAD history for this archived tenancy (read-only)."}
          </span>
        )}
        {itemId && entries.length > 0 && (
          <button
            type="button"
            className={`pdi-link${canAdd ? "" : " ms-auto"}`}
            title="Open a printable record of every PAD entry and its history"
            onClick={() => {
              const q = new URLSearchParams({
                unit: unitNo || "",
                property: propertyName || "",
                building: buildingName || "",
              });
              window.open(`/pad-history-print/${itemId}?${q.toString()}`, "_blank", "noopener,noreferrer");
            }}
          >
            <FeatherIcon icon="printer" size={13} /> Print history
          </button>
        )}
        {(canAdd || canAddArchived) && (
          <RButton color="primary" className="ms-auto" onClick={() => { setEditing(null); setModalOpen(true); }}>
            {archivedItemId ? "+ Record PAD Stop" : "+ Add PAD info"}
          </RButton>
        )}
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" /> <span className="text-muted ms-2">Loading…</span>
        </div>
      ) : visible.length === 0 ? (
        <Alert color={archivedItemId ? "light" : "warning"} className="my-2">
          {archivedItemId
            ? `No PAD info was recorded for this tenancy.${canAddArchived ? " If the resident's PAD still needs stopping, click “Record PAD Stop”." : ""}`
            : canAdd
              ? "No PAD info on file for this resident yet. Click “Add PAD info” to record the New PAD, a change, a delay, a stop…"
              : "No PAD info on file for this resident."}
          {hiddenCount > 0 && !showHistory ? ` (${hiddenCount} in history)` : ""}
        </Alert>
      ) : (
        <div className="pdi-wrap">
          <div className="pdi-scroll">
            <table className="pdi-table">
              <thead>
                <tr>
                  <th className="num w-sno">S.No</th>
                  <th>Category</th>
                  <th className="w-eff">Effective</th>
                  <th>Details</th>
                  <th>Notes</th>
                  {(!archivedItemId || canAddArchived) && <th className="num w-act">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((e, i) => {
                  const busy = busyId === e._id;
                  const inactive = e.voided || e.tenancy_archived;
                  const sNo = visible.length - i;
                  // In archived mode the tenancy's history stays frozen except its PAD
                  // Stop, which is still live work for accounting (mirrors the server).
                  const editable = canWrite && !readOnly && !e.voided &&
                    (archivedItemId ? e.category === "STOP" : !inactive);
                  const showActions = !archivedItemId || canAddArchived;
                  const colSpan = showActions ? 6 : 5;
                  return (
                    <React.Fragment key={e._id}>
                    <tr className={inactive && !archivedItemId ? "muted" : ""}>
                      <td className="num">{sNo}</td>
                      <td className="pdi-cat">
                        <CategoryPill category={e.category} />
                        {!inactive && isScheduled(e) && <span className="pdi-tag pdi-tag-sched">Scheduled</span>}
                        {e.voided && <span className="pdi-tag">Voided</span>}
                        {e.tenancy_archived && !archivedItemId && <span className="pdi-tag">Previous resident</span>}
                        <div className="pdi-id">{e.pad_id}</div>
                        {e.tenant_names ? <div className="pdi-meta">{e.tenant_names}</div> : null}
                      </td>
                      <td className="pdi-date">
                        {e.effective_date ? formatYmd(e.effective_date) : "—"}
                        {/* A Delay's day is its date, a Split's days are its pulls — saying "pull day" again is noise. */}
                        {e.pull_day && e.pull_day !== 1 && e.category !== "DELAY" && e.category !== "SPLIT" ? <div className="pdi-meta">pull day {e.pull_day}</div> : null}
                      </td>
                      <td>
                        <div className="pdi-details">{formatEntryDetails(e)}</div>
                        {e.voided && e.void_reason ? <div className="pdi-meta">Void reason: {e.void_reason}</div> : null}
                        <div className="pdi-meta">
                          Recorded {shortDate(e.createdAt)}{e.created_by?.name ? ` · ${e.created_by.name}` : ""}
                        </div>
                        {(e.attachments || []).filter((a) => a && !a.removed).length > 0 && (
                          <div className="pdi-meta pdi-attach">
                            <FeatherIcon icon="paperclip" size={11} />
                            {(e.attachments || []).filter((a) => a && !a.removed).length} attachment
                            {(e.attachments || []).filter((a) => a && !a.removed).length === 1 ? "" : "s"}
                          </div>
                        )}
                        <button type="button" className="pdi-hist-toggle" onClick={() => toggleHistory(e._id)}>
                          <FeatherIcon icon={openHistory.has(e._id) ? "chevron-down" : "chevron-right"} size={11} />
                          History ({(e.audit_trail || []).length})
                        </button>
                      </td>
                      <td>
                        <div className="pdi-notes">{e.notes || <span style={{ color: "#B0B8C4" }}>—</span>}</div>
                      </td>
                      {showActions && (
                        <td>
                          <div className="pdi-actions">
                            {busy && <Spinner size="sm" />}
                            {editable && (
                              <button className="pdi-btn" onClick={() => { setEditing(e); setModalOpen(true); }} disabled={busy}>
                                <FeatherIcon icon="edit-2" size={13} /> Edit
                              </button>
                            )}
                            {editable && (
                              <button className="pdi-btn pdi-btn-danger" onClick={() => handleVoid(e)} disabled={busy}>
                                Void
                              </button>
                            )}
                            {!editable && <span className="pdi-meta">—</span>}
                          </div>
                        </td>
                      )}
                    </tr>
                    {openHistory.has(e._id) && (
                      <tr className="pdi-hist-row">
                        <td colSpan={colSpan}>
                          <PadHistory audit={e.audit_trail} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <PadInfoModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          itemId={itemId}
          archivedItemId={archivedItemId}
          item={item}
          unitNo={unitNo}
          buildingName={buildingName}
          propertyName={propertyName}
          tenantNames={tenantNames}
          incoming={incoming}
          existing={editing}
          // The unit's last addendum, as the server reads it: a New Rent Amount takes its amount
          // and its start date from there.
          rentChange={unitStatus?.rent_change || null}
          onSaved={refresh}
        />
      )}
    </div>
  );
};

/**
 * Collapsible "PAD Info" section for the tenant modal — mirrors the Lease Addendums
 * section (pill button + Collapse) and adds a live PAD-state badge on the pill.
 *
 * Props:
 *  - itemId/item: the mastersheet unit (live mode)
 *  - archivedItemId: an archived tenancy (read-only history mode)
 *  - readOnly: hide write actions; canWrite: role capability (verification lives on the PAD report)
 *  - initialOpen: start expanded (deep link from the notification center)
 */
const PadInfo = ({
  propertyId, buildingName, propertyName, unitNo, itemId, item, tenants, archivedItemId,
  readOnly = false, canWrite = true, initialOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(!!initialOpen);
  const [status, setStatus] = useState(null);

  const tenantNames = useMemo(() => {
    if (Array.isArray(tenants) && tenants.length) {
      return tenants.map((t) => (typeof t === "string" ? t : t?.name || `${t?.first_name || t?.firstName || ""} ${t?.last_name || t?.lastName || ""}`.trim())).filter(Boolean).join(", ");
    }
    if (typeof item?.tenant === "string") return item.tenant;
    if (typeof item?.tenants === "string") return item.tenants;
    return "";
  }, [tenants, item]);

  const loadStatus = useCallback(() => {
    if (!itemId || archivedItemId) return;
    getUnitPadStatus(itemId)
      .then((res) => setStatus(res.data?.data || null))
      .catch(() => setStatus(null));
  }, [itemId, archivedItemId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (initialOpen) setIsOpen(true);
  }, [initialOpen]);

  return (
    <RCard className="mb-4" id="pad-info-section">
      <RCardHeader className="py-3 text-center">
        <RButton color="primary" outline className="fw-bold px-4 py-2 rounded-pill" onClick={() => setIsOpen((p) => !p)}>
          <FeatherIcon icon="credit-card" className="me-2" size={16} />
          PAD Info
        </RButton>
        {!archivedItemId && <StatePill status={status} />}
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          {!itemId && !archivedItemId ? (
            <Alert color="info" className="my-2">
              Save the tenant first to record PAD info for this unit.
            </Alert>
          ) : (
            <PadInfoList
              itemId={itemId}
              item={item}
              archivedItemId={archivedItemId}
              propertyId={propertyId}
              buildingName={buildingName}
              propertyName={propertyName}
              unitNo={unitNo}
              tenantNames={tenantNames}
              incoming={status?.incoming || null}
              readOnly={readOnly}
              canWrite={canWrite}
              onStatusChange={loadStatus}
              unitStatus={status}
            />
          )}
        </CardContent>
      </RCollapse>
    </RCard>
  );
};

export { PadInfo };
export default PadInfo;
